// Imports
import { Add, ArrowBack, MenuBookOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { parseErrorForAxios } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { AccessoryModel } from '../utils/Models';
import { AccessoryTable } from './AccessoryTable';
import { AccessoryDetails } from './AccessoryDetails';
import { Colors, DefaultSliceData, DEFAULT_LOADER_STYLES } from '../../config/Constants';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { setNavSelection } from '../../features/navSelection';

// Types and constants
type AccessoryDashboardView = 'all' | 'individual' | 'create';
const defaultView: AccessoryDashboardView = 'all';
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  view: AccessoryDashboardView;
  accData?: AccessoryModel;
}

// Main app component
export function AccessoryDashboard(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [localState, setLocalState] = useState<LocalState>({ view: defaultView });
  const [accessoryData, setAccessoryData] = useState<AccessoryModel[]>([]);

  // Local variables based on state
  const accessoryDetailsView = localState.view === 'individual' && localState.accData;

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const dispatch = useDispatch();

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(5));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load orders on first load
  const loadAccessories = () => {
    const authCookie = getAuthCookie();
    if (!selectedHub?.id || !authCookie) return;
    setLocalState({ loading: true, message: '', view: defaultView });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/accessories`, { headers: { 'vendor-token': authCookie } })
        const accessoryList: AccessoryModel[] = data.data[0].accessories;
        setLocalState({ view: defaultView });
        setAccessoryData(accessoryList);
      } catch (e) {
        setLocalState({ error: true, message: parseErrorForAxios(e), view: defaultView });
      }
    })();
  }
  useEffect(loadAccessories, [selectedHub]);

  // Delete accessory
  const deleteAccessory = (accessoryId: number) => {
    console.log('called')
    const authCookie = getAuthCookie();
    setLocalState({ loading: true, view: defaultView });
    (async () => {
      try {
        await GobikesApi.delete(`/vendors/hubs/${selectedHub.id}/accessories/${accessoryId}`, { headers: { 'vendor-token': authCookie } });
        setLocalState({ loading: false, view: defaultView })
        loadAccessories();
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setLocalState({ error: true, message: errorMessage, loading: false, view: defaultView })
      }
    })();
  }

  // Loading view
  let mainContent = <div></div>;
  if (localState.loading) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (localState.error) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // All accessories view
  else if (localState.view === 'all') {
    mainContent = <Fragment>
      <Box className='gbk-tabs' sx={{ typography: 'body1', m: '30px 0 0 0' }}>
        <Box className="phone-fab">
          <Button className="thick-border " variant="outlined" onClick={e => setLocalState({ view: 'create' })}><Add /> Add</Button>
        </Box>
        <AccessoryTable
          accessoryList={accessoryData}
          setParentState={setLocalState}
          containerClass={'gbk-grid-parent no-fabs'}
          deleteAccessory={deleteAccessory}
        />
      </Box >
    </Fragment >
  }

  // Individual accessory view
  else if (localState.view === 'individual') {
    if (!localState.accData) {
      setLocalState({ error: true, message: 'Invalid accessory data', view: defaultView });
    } else {
      mainContent = (
        <AccessoryDetails
          accessory={localState.accData}
          backFn={(reload?: boolean) => { if (reload) return loadAccessories(); if (accessoryDetailsView) setLocalState({ view: defaultView, accData: undefined }) }}
          drawerWidth={props.drawerWidth}
          editMode={false}
          creating={false}
        />
      )
    }
  }

  // Create accessory view
  else if (localState.view === 'create') {
    mainContent = (
      <AccessoryDetails
        accessory={DefaultSliceData.DUMMY_ACCESSORY}
        backFn={(reload?: boolean) => { if (reload) return loadAccessories(); if (accessoryDetailsView) setLocalState({ view: defaultView, accData: undefined }) }}
        drawerWidth={props.drawerWidth}
        editMode={true}
        creating={true}
      />
    )
  }

  // Default view
  else {
    mainContent = <div></div>;
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className={`gbk-tabcontent ${localState.view === 'all' ? '' : 'allow-scroll'}`}>

        {/* Add accessory fab */}
        <Button
          onClick={e => setLocalState({ view: 'create' })}
          className='gbk-fab gbk-fab-top-right'
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          variant='contained'
          size='large'
          color='primary'
        >
          <Add /> Add Accessory
        </Button>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div
            className={`tab-header-fab ${accessoryDetailsView ? 'clickable' : ''}`}
            onClick={() => { if (accessoryDetailsView) setLocalState({ view: defaultView, accData: undefined }) }} >
            {
              accessoryDetailsView
                ? <ArrowBack fontSize="large" />
                : <MenuBookOutlined fontSize="large" />
            }
          </div>

          {/* Text */}
          <Typography>
            <span style={{ ...(accessoryDetailsView && { color: Colors.primary }) }}>
              Accessories
            </span>
            {accessoryDetailsView ? ' / Accessory Details' : null}
          </Typography>
        </Box>

        {/* Main content */}
        {mainContent}

      </div >
    </Box >
  )

}